/**
 * The nine status columns (prototype `vKanban`'s `.kb`; Task 7 adds `Comparison Done` as its own
 * column, matching the server's real status vocabulary 1:1 — see `lib/kanban.ts`).
 *
 * Columns are fixed and always shown, empty or not — an empty column is information, and a
 * board whose columns move around is harder to scan.
 */

import { RequirementCard } from './RequirementCard'
import type { BoardColumn } from '../../lib/kanban'
import type { EnquiryReplyState } from '../../lib/boardReplies'
import type { EnquirySummary } from '../../lib/types'

export function KanbanBoard({
  columns,
  canDecide,
  isDeciding,
  decidingQuotationId,
  repliesByEnquiry,
  onOpen,
  onDecide,
  onDuplicate,
  duplicatingId,
  onSync,
  syncingId,
}: {
  columns: BoardColumn[]
  canDecide: boolean
  /** Any decision in flight — the others are disabled so two can't race. */
  isDeciding: boolean
  decidingQuotationId: string | null
  /** Only the watched columns have entries; a card with none simply shows no chip. */
  repliesByEnquiry: Map<string, EnquiryReplyState>
  onOpen: (requirement: EnquirySummary) => void
  onDecide: (requirement: EnquirySummary, to: 'Won' | 'Rejected') => void
  onDuplicate?: (requirement: EnquirySummary) => void
  duplicatingId?: string | null
  /** Re-send a won chain to the ERP — see `lib/wonDecision.ts`. */
  onSync?: (requirement: EnquirySummary) => void
  syncingId?: string | null
}) {
  return (
    <div className="kb">
      {columns.map((column) => (
        <div className="kcol" key={column.status}>
          <h3>
            {column.status}
            <span className="n">{column.cards.length}</span>
          </h3>
          <div className="klist">
            {column.cards.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 12, padding: 6 }}>—</div>
            ) : (
              column.cards.map(({ requirement, effective }) => (
                <RequirementCard
                  key={requirement.id}
                  requirement={requirement}
                  // Carried from the grouping, not recomputed — see lib/kanban.
                  effectiveStatus={effective}
                  canDecide={canDecide}
                  deciding={decidingQuotationId === requirement.quotation_id}
                  decideDisabled={isDeciding}
                  replies={repliesByEnquiry.get(requirement.id) ?? null}
                  onOpen={() => onOpen(requirement)}
                  onDecide={(to) => onDecide(requirement, to)}
                  onDuplicate={onDuplicate ? () => onDuplicate(requirement) : undefined}
                  duplicating={duplicatingId === requirement.id}
                  onSync={onSync ? () => onSync(requirement) : undefined}
                  syncing={syncingId === requirement.id}
                />
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
